// Render scheduling, viewport management, canvas setup
import type { EditorState } from "./state/EditorState";
import type { Canvas2DRenderer } from "../core/render/Canvas2DRenderer";
import type { Tool, ToolContext } from "../core/tools/Tool";
import type { Guide } from "../core/render/Canvas2DRenderer";
import { fitToScreen, zoomAt, screenToDoc } from "../core/render/Viewport";

export interface RenderDeps {
  state: EditorState;
  renderer: Canvas2DRenderer;
  canvas: HTMLCanvasElement;
  cursorCanvas: HTMLCanvasElement;
  viewportEl: HTMLElement;
  toolUx: { setTool: (id: string, tool: Tool) => void; drawCursorInternal: () => void; setCursorPos: (x: number, y: number) => void };
  updateStatusBar: () => void;
  updateDocTab: () => void;
  updateSwatches: () => void;
  syncColorPanel: () => void;
  renderLayersPanel: () => void;
  syncLayerControls: () => void;
  syncCharPanel: () => void;
  syncPropertiesPanel: () => void;
  renderChannelsPanel: () => void;
  updateDropHint: () => void;
  updateCursor: () => void;
  updateNavigatorPanel: () => void;
  updateHistogramPanel: () => void;
  updateOverlay: () => void;
  showRulers: boolean;
  updateRulers: () => void;
}

let frameScheduled = false;

export function scheduleFrame(deps: RenderDeps): void {
  if (frameScheduled) return;
  frameScheduled = true;
  requestAnimationFrame(() => {
    frameScheduled = false;
    deps.renderer.render(deps.state.doc, deps.state.viewport);
    deps.updateStatusBar();
    deps.updateDocTab();
    deps.updateSwatches();
    deps.syncColorPanel();
    deps.renderLayersPanel();
    deps.syncLayerControls();
    deps.syncCharPanel();
    deps.syncPropertiesPanel();
    deps.renderChannelsPanel();
    deps.updateDropHint();
    deps.updateCursor();
    deps.updateNavigatorPanel();
    deps.updateHistogramPanel();
    // Tool UX: detect active tool changes
    const active = deps.state.activeTool;
    if (active && active.id !== (deps.toolUx as any).lastToolId) {
      (deps.toolUx as any).lastToolId = active.id;
      deps.toolUx.setTool(active.id, active);
    }
    deps.toolUx.drawCursorInternal();
    // Tool overlays
    deps.updateOverlay();
    // Update rulers if visible
    if (deps.showRulers) deps.updateRulers();
  });
}

export function resize(deps: {
  viewportEl: HTMLElement;
  renderer: Canvas2DRenderer;
  cursorCanvas: HTMLCanvasElement;
  state: EditorState;
}): void {
  const r = deps.viewportEl.getBoundingClientRect();
  const newW = r.width;
  const newH = r.height;

  // Capture old viewport size from the canvas backing before we resize it.
  const dpr = window.devicePixelRatio || 1;
  const oldW = deps.cursorCanvas.width / dpr;
  const oldH = deps.cursorCanvas.height / dpr;

  deps.renderer.resize(newW, newH, dpr);
  deps.cursorCanvas.width = newW * dpr;
  deps.cursorCanvas.height = newH * dpr;
  deps.cursorCanvas.style.width = newW + "px";
  deps.cursorCanvas.style.height = newH + "px";

  // Re-center the doc in the new viewport so it doesn't drift off-screen.
  // We keep the same doc-space point that was at the OLD viewport center and
  // place it at the NEW viewport center. This preserves the user's zoom level
  // while adjusting only pan.
  if (deps.state.doc && oldW > 0 && oldH > 0) {
    const vp = deps.state.viewport;
    // Doc-space coordinate that was at the center of the old viewport.
    const docCenterX = (oldW / 2 - vp.panX) / vp.zoom;
    const docCenterY = (oldH / 2 - vp.panY) / vp.zoom;
    // New pan that puts that same doc point at the center of the new viewport.
    const newPanX = newW / 2 - docCenterX * vp.zoom;
    const newPanY = newH / 2 - docCenterY * vp.zoom;
    deps.state.setViewport({ panX: newPanX, panY: newPanY });
  }

  deps.renderer.render(deps.state.doc, deps.state.viewport);
}

export function zoomCentered(state: EditorState, factor: number, canvas: HTMLCanvasElement): void {
  const r = canvas.getBoundingClientRect();
  state.setViewport(zoomAt(state.viewport, factor, r.width / 2, r.height / 2));
}

export function fitDoc(state: EditorState, viewportEl: HTMLElement): void {
  if (!state.doc) return;
  const r = viewportEl.getBoundingClientRect();
  // Issue #90: fitToScreen subtracts padding (default 32) from the viewport
  // before dividing by the doc dimensions.  For a small/narrow viewport
  // (e.g. when panels have compressed the canvas area) or a very small doc,
  // `viewportWidth - padding*2` can go negative and the resulting zoom is
  // negative — the canvas then renders inverted/blank and the doc tab reads
  // a nonsense "−650%".  Clamp the returned zoom into Photoshop's sensible
  // range (1% … 32000%, matching the PS zoom HUD).  Re-center the canvas
  // so a tiny doc on a tiny viewport still shows up cleanly.
  const raw = fitToScreen(state.doc.width, state.doc.height, r.width, r.height);
  const ZOOM_MIN = 0.01; // 1%
  const ZOOM_MAX = 320;  // 32000% — matches Photoshop's max
  const safeZoom = Number.isFinite(raw.zoom) && raw.zoom > 0
    ? Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, raw.zoom))
    : 1;
  state.setViewport({
    zoom: safeZoom,
    panX: (r.width - state.doc.width * safeZoom) / 2,
    panY: (r.height - state.doc.height * safeZoom) / 2,
  });
}

export function resetTo100(state: EditorState, viewportEl: HTMLElement): void {
  if (!state.doc) return;
  const r = viewportEl.getBoundingClientRect();
  state.setViewport({
    zoom: 1,
    panX: (r.width - state.doc.width) / 2,
    panY: (r.height - state.doc.height) / 2,
  });
}

export function updateDocTab(state: EditorState, closeDoc: () => void): void {
  const tabs = document.querySelector(".doc-tabs");
  if (!tabs) return;
  // No document: clear all tab content. This covers:
  //   - Cold start before any document is opened (issue #50)
  //   - After the user closes the active doc via Ctrl+W or × button (issue #68)
  // The previous version unconditionally created a placeholder tab, which
  // left a stale title behind. innerHTML="" detaches any cached close-button
  // listeners as well, so no leak.
  if (!state.doc) {
    tabs.innerHTML = "";
    document.title = "Pixelpa";
    return;
  }
  let tab = tabs.querySelector<HTMLDivElement>(".doc-tab");
  if (!tab) {
    tab = document.createElement("div");
    tab.className = "doc-tab is-active";
    tabs.appendChild(tab);
  }
  const name = state.doc.name;
  // i45: Photoshop-style unsaved-changes marker — a leading "•" on the doc
  // tab and the same marker on the browser tab title, both driven by
  // state.dirty (set true on any EditorState.execute(), cleared by
  // markSaved()/setDocument()). No new wiring needed: this runs on every
  // scheduleFrame() pass already, i.e. after every state change.
  const dirtyMarker = state.dirty ? "• " : "";
  tab.innerHTML = `${dirtyMarker}${escapeHtml(name)} <span class="muted">@ ${Math.round(state.viewport.zoom * 100)}% (RGB/8)</span><span class="doc-tab__close" title="Close">×</span>`;
  tab.querySelector<HTMLSpanElement>(".doc-tab__close")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    closeDoc();
  });
  document.title = `${dirtyMarker}${name} — Pixelpa`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}
